import React from 'react'
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import StarIcon from '@mui/icons-material/Star'
import EventIcon from '@mui/icons-material/Event'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { DemoChip, TrialledChip, SoftChip } from '../kit/Chips'
import { followUpState, fromNow, interestColor } from '../kit/helpers'
import OwnerAvatar from '../kit/OwnerAvatar'

// Pipeline board card. Pure presentation - drag handling and menus live in
// the board.
const LeadCard = ({ row, onOpen, onMenu, dragging = false }) => {
  const followUp = followUpState(row.nextFollowUpAt)
  const tenantDisabled = row.tenant && row.tenant.isActive === false
  return (
    <Paper
      elevation={dragging ? 8 : 0}
      onClick={onOpen}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        cursor: 'pointer',
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 4px 14px rgba(15,23,42,0.08)' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={0.5}>
        <Box minWidth={0} flex={1}>
          <Typography fontWeight={700} lineHeight={1.25} sx={{ wordBreak: 'break-word' }}>
            {row.organisationName || 'Unnamed organisation'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {row.contactName || 'No contact'}
            {row.contactEmail ? ` · ${row.contactEmail}` : ''}
          </Typography>
        </Box>
        {onMenu && (
          <IconButton
            size="small"
            aria-label="Lead actions"
            onClick={(event) => {
              event.stopPropagation()
              onMenu(event)
            }}
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {(row.demoAt || row.trialled || tenantDisabled || row.tenant?.isDemo || row.source) && (
        <Stack direction="row" gap={0.5} flexWrap="wrap" marginTop={1}>
          {row.demoAt && <DemoChip demoAt={row.demoAt} sx={{ height: 22 }} />}
          {row.trialled && <TrialledChip trialEnd={row.trialEnd} sx={{ height: 22 }} />}
          {tenantDisabled && <SoftChip color="#E32525" label="Tenant disabled" sx={{ height: 22 }} />}
          {row.source && !row.trialled && (
            <SoftChip color="#64748B" label={row.source} sx={{ height: 22 }} />
          )}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" gap={1.25} marginTop={1.25} flexWrap="wrap">
        {row.interestLevel ? (
          <Tooltip title={`Interest ${row.interestLevel}/5`}>
            <Stack direction="row" alignItems="center" gap={0.25}>
              <StarIcon sx={{ fontSize: 15, color: interestColor(row.interestLevel) }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {row.interestLevel}
              </Typography>
            </Stack>
          </Tooltip>
        ) : null}
        {followUp && (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <EventIcon sx={{ fontSize: 15, color: followUp.color }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: followUp.color }}>
              {followUp.label}
            </Typography>
          </Stack>
        )}
        {row.activityCount > 0 && (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {row.activityCount}
            </Typography>
          </Stack>
        )}
        <Box flex={1} />
        <Typography variant="caption" color="text.disabled" noWrap>
          {row.lastContactedAt ? `contacted ${fromNow(row.lastContactedAt)}` : `added ${fromNow(row.createdAt)}`}
        </Typography>
        {row.leadId && <OwnerAvatar owner={row.owner} size={20} />}
      </Stack>
    </Paper>
  )
}

export default LeadCard
